import { Phone, CalendarCheck, ShieldCheck, Clock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE, IMAGES } from "@/lib/site";

const heroImage = IMAGES.hero;

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#E0F2FE] blur-3xl opacity-70" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#CCFBF1] blur-3xl opacity-60" />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 pt-10 md:pt-20 pb-16 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0F9FF] border border-[#BAE6FD]">
            <Sparkles className="w-3.5 h-3.5 text-[#0369A1]" />
            <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#0369A1]">
              NABL-aligned · Bangalore
            </span>
          </div>

          <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0B2545] leading-[1.05]">
            Accurate Diagnostics.
            <br />
            <span className="text-[#0369A1]">Trusted Care.</span>
          </h1>

          <p className="mt-5 text-base sm:text-lg text-[#475569] max-w-xl leading-relaxed">
            Advanced ultrasound &amp; imaging with expert radiologist reporting — pregnancy,
            Doppler, thyroid, breast &amp; pelvic scans, with a female consultant radiologist
            at our Hosa Road branch.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="hero-book-btn"
              onClick={() => document.getElementById("appointment")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full bg-[#0369A1] hover:bg-[#0284C7] text-white h-12 px-7 text-base font-semibold shadow-sm hover:shadow"
            >
              <CalendarCheck className="w-5 h-5 mr-2" /> Book Appointment
            </Button>
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full border-2 border-[#0B2545] text-[#0B2545] hover:bg-[#F8FAFC] text-base font-semibold transition-colors"
            >
              <Phone className="w-5 h-5" /> Call Now
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[
              { k: "25K+", v: "Patients served" },
              { k: "60+", v: "Tests & scans" },
              { k: "2", v: "Bangalore centres" },
            ].map((s) => (
              <div key={s.v} className="border-l-2 border-[#14B8A6] pl-3">
                <div className="font-display text-2xl font-bold text-[#0B2545]">{s.k}</div>
                <div className="text-xs text-[#64748B] mt-0.5">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#E0F2FE] to-[#CCFBF1] rounded-[2rem] -z-10" />
            <img
              src={heroImage}
              alt="Female radiologist performing a diagnostic ultrasound for a patient at Vinodha Diagnostics"
              className="w-full h-[420px] sm:h-[480px] lg:h-[560px] object-cover rounded-[1.75rem] shadow-xl"
              loading="eager"
            />

            <div className="absolute -bottom-5 left-5 right-5 sm:left-8 sm:right-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-5 flex items-center gap-4 max-w-sm">
              <div className="grid place-items-center w-11 h-11 rounded-xl bg-[#F0FDFA] text-[#0D9488]">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-[#0B2545]">Senior Radiologists On-site</div>
                <div className="text-xs text-[#64748B] flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" /> Twice daily · Mon–Sat
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
